import { commonOptions } from '@/config/options'
import { PromotionType, ScopeType } from '@/config/enums'
import type { PageContext } from '@zto/zpage'
import dayjs from 'dayjs'

// 新增、编辑推广单
export const addEditDialog = (type: string) => {
  const isAdd = type === 'add' // 新增
  const isView = type === 'view' // 详情

  const actions = isView
    ? {
        close: {}
      }
    : {
        close: {},
        submit: { label: '确定' }
      }

  return {
    width: 800,
    labelWidth: 120,
    itemSpan: 24,

    onShow: async ({ app, data }: PageContext) => {
      if (isAdd) {
        return {
          payload: {
            promotionType: PromotionType.ADVERTISE, // 默认选中广告推广
            scopeType: 'ALL_SHOP' // 默认选中所有门店生效
          }
        }
      }
      const res = await app.request({
        api: '/cabinet/admin/oms/AdminMktPromotionQueryController/getDetail',
        params: { promotionCode: data.promotionCode }
      })

      let areaCodes = undefined
      let depotCode = undefined
      let productCode = res?.productInfo?.productCode || undefined
      let productName = res?.productInfo?.productName || undefined
      let templateCode = res?.productInfo?.templateCode || undefined
      let templateName = res?.productInfo?.templateName || undefined

      if (res.scopeType === ScopeType.LOCATION) {
        areaCodes = res.scopeInfos
      } else if (res.scopeType === ScopeType.PART_SHOP) {
        depotCode = res.scopeInfos?.map((item: any) => item.shopCode)?.join('\n')
      }

      return {
        payload: {
          ...res,
          pictureUrl: res.pictureUrl ? [res.pictureUrl] : [],
          areaCodes,
          depotCode,
          productCode,
          productName,
          templateCode,
          templateName
        }
      }
    },

    formItems: [
      {
        type: 'text',
        prop: 'promotionCode',
        label: '推广单ID',
        visibleOn: `${!isAdd}` // 编辑、详情时显示
      },
      {
        type: 'checkbox',
        checkType: 'radio',
        prop: 'promotionType',
        label: '推广类型',
        options: commonOptions.promotionType,
        default: PromotionType.ADVERTISE, // 默认选中广告推广
        disabled: isView,
        required: true,
        onChange: async (model: any, val: any) => {
          // 广告推广时，宣传物料 数据处理
          if (model.promotionType === PromotionType.ADVERTISE) {
            model.pictureUrl = model?.pictureUrl?.length ? model.pictureUrl : undefined
          }
        }
      },
      {
        type: 'fuzzy-select',
        prop: 'productCode',
        labelProp: 'productName',
        label: '商品选择',
        api: '/cabinet/admin/pms/AdminPmsProductQueryController/page',
        apiParams: {
          current: 1,
          size: 500
        },
        optionValueProp: 'productCode',
        optionLabelProp: 'productName',
        triggerFocus: true,
        remote: true,
        keywordProp: 'productName',
        visibleOn: `this.data.promotionType === '${PromotionType.PRODUCT}'`,
        dynamicAttrs: ({ data }: PageContext) => {
          return {
            required: data.promotionType === PromotionType.PRODUCT
          }
        },
        disabled: isView
      },
      {
        type: 'fuzzy-select',
        prop: 'templateCode',
        labelProp: 'templateName',
        label: '模板选择',
        api: '/cabinet/admin/mkt/AdminMktProductTemplateQueryController/page',
        apiParams: {
          current: 1,
          size: 500
        },
        optionValueProp: 'templateCode',
        optionLabelProp: 'templateName',
        triggerFocus: true,
        remote: true,
        keywordProp: 'templateName',
        visibleOn: `this.data.promotionType === '${PromotionType.PRODUCT}'`,
        dynamicAttrs: ({ data }: PageContext) => {
          return {
            required: data.promotionType === PromotionType.PRODUCT
          }
        },
        disabled: isView
      },
      {
        type: 'checkbox',
        checkType: 'radio',
        prop: 'scopeType',
        label: '生效范围',
        options: commonOptions.scopeType,
        default: ScopeType.ALL_SHOP, // 默认选中所有门店生效
        disabled: isView,
        required: true
      },
      {
        type: 'area-pro',
        prop: 'areaCodes',
        label: '省市区',
        multiple: true, // 支持多选
        visibleOn: `this.data.scopeType === '${ScopeType.LOCATION}'`,
        dynamicAttrs: ({ data }: PageContext) => {
          return {
            required: data.scopeType === ScopeType.LOCATION
          }
        },
        disabled: isView
      },
      {
        type: 'depot-code-input',
        label: '门店编号',
        autoHeight: true,
        visibleOn: `this.data.scopeType === '${ScopeType.PART_SHOP}'`,
        dynamicAttrs: ({ data }: PageContext) => {
          return {
            required: data.scopeType === ScopeType.PART_SHOP
          }
        },
        disabled: isView
      },
      {
        type: 'input',
        prop: 'broadcastDesc',
        label: '语音播报文案',
        maxlength: 15,
        showWordLimit: true,
        placeholder: '请输入语音播报文案 (最多15个中文字符)',
        disabled: isView
      },
      {
        type: 'html',
        // labelWidth: 0,
        html: '<div style="color: #909399; font-size: 12px">设置语言播报文案后，支持语音播报的设备将在展示推广的同时进行语音播报</div>'
      },
      {
        label: '宣传物料',
        type: 'upload',
        prop: 'pictureUrl',
        multiple: false,
        accept: '.jpeg,.jpg,.png,.gif',
        uploadAttrs: {
          dialogTitle: '上传宣传物料',
          tip: '支持jpg、png、jpeg、gif格式，尺寸要求850 * 678，大小1M以内 ',
          sizeLimit: 1,
          aspectLimit: [850, 678]
        },
        listAttrs: {
          // enhance: true, // 增强属性不支持gif
          readonly: isView
        },
        listType: 'image', // 图片格式
        countLimit: 1,
        autoHeight: true,
        visibleOn: `this.data.promotionType === '${PromotionType.ADVERTISE}'`,
        dynamicAttrs: ({ data }: PageContext) => {
          return {
            required: data.promotionType === PromotionType.ADVERTISE
          }
        }
      }
    ],

    beforeSubmit: async ({ app, data }: PageContext, model: any, options: any) => {
      // 处理宣传物料
      if (data.pictureUrl && Array.isArray(data.pictureUrl)) {
        data.pictureUrl = data.pictureUrl[0] || ''
      }

      if (data.scopeType === ScopeType.ALL_SHOP) {
        // 所有门店
        data.scopeInfos = []
      } else if (data.scopeType === ScopeType.LOCATION) {
        // 省市区
        data.scopeInfos = data.areaCodes
      } else if (data.scopeType === ScopeType.PART_SHOP) {
        // 门店编号
        data.scopeInfos = data.depotCode?.map((item: any) => ({ shopCode: item }))
      }

      data.productInfo = {
        productCode: data.productCode,
        templateCode: data.templateCode
      }

      if (data.areaCodes) delete data.areaCodes

      if (data.depotCode) delete data.depotCode

      if (data.productCode) delete data.productCode
      if (data.productName) delete data.productName
      if (data.templateCode) delete data.templateCode
      if (data.templateName) delete data.templateName

      if (data.creator) delete data.creator
      if (data.gmtCreate) delete data.gmtCreate
      if (data.modifier) delete data.modifier
      if (data.gmtModified) delete data.gmtModified

      return true
    },

    actions
  }
}

export function logDialog() {
  return {
    width: 600,
    top: '10vh',
    labelWidth: 0,
    itemSpan: 24,

    onShow: async ({ app, data }: PageContext) => {
      const res = await app.request({
        api: '/cabinet/admin/base/AdminBaseCommonQueryController/queryAuditLogPage',
        params: { bizNo: data.promotionCode, logScene: 'mkt.promotion_operate', current: 1, size: 100 }
      })

      return {
        payload: {
          operateLogData: res.data
        }
      }
    },

    formItems: [
      {
        label: '',
        labelWidth: 0,
        type: 'edit-table',
        prop: 'operateLogData',
        span: 24,
        // height: 300,
        noOperation: true,
        autoHeight: true,
        border: true,
        columns: [
          {
            label: '操作时间',
            minWidth: 150,
            formatter: (row: any) => {
              return row.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--'
            }
          },
          { prop: 'logDesc', label: '操作名称', minWidth: 160 },
          { prop: 'creator', label: '操作人', minWidth: 100 }
        ]
      }
    ],

    actions: {
      close: {}
    }
  }
}
